/**
 * Main Public Application Controller
 * Initializes public-facing JavaScript modules
 */
import { RegistrationFormManager } from './events/registration.js';
import { initModalLoader } from './templates/modal-loader.js';
import htmx from 'htmx.org';

// HTMX 2 ESM does not set window.htmx; other scripts and htmx.process() need it.
window.htmx = htmx;

/**
 * Set active state for menu links based on current URL
 * Works for both header-nav and sticky menu
 */
function setActiveMenuLinks(container = document) {
    const currentPath = window.location.pathname;
    const currentPathNormalized = currentPath.endsWith('/') && currentPath !== '/' 
        ? currentPath.slice(0, -1) 
        : currentPath;
    
    // Find all links in header-nav (for render_menu) and sticky menu
    const menuLinks = container.querySelectorAll('.header-nav a[href], nav.menu a[href]');
    
    menuLinks.forEach(link => {
        let href = link.getAttribute('href');
        if (!href) return;

        if (link.classList.contains('submenu-link')) return;
        
        // Remove active class from all links first
        link.classList.remove('active');
        
        // Handle relative URLs
        let linkPath;
        try {
            // If href is absolute or has protocol, use URL constructor
            if (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('//')) {
                linkPath = new URL(href).pathname;
            } else {
                // For relative URLs, construct full URL
                linkPath = new URL(href, window.location.origin).pathname;
            }
        } catch (e) {
            // Fallback: treat as relative path
            linkPath = href.startsWith('/') ? href : '/' + href;
        }
        
        // Normalize path (remove trailing slash except for root)
        const linkPathNormalized = linkPath.endsWith('/') && linkPath !== '/' 
            ? linkPath.slice(0, -1) 
            : linkPath;
        
        // Check for exact match
        const isExactMatch = linkPathNormalized === currentPathNormalized;
        
        // Check if current path starts with link path (for nested pages)
        // But exclude root path '/' to avoid matching everything
        const isNestedMatch = linkPathNormalized !== '/' && 
                             linkPathNormalized !== '' && 
                             currentPathNormalized.startsWith(linkPathNormalized);
        
        if (isExactMatch || isNestedMatch) {
            link.classList.add('active');
        }
    });
}

class PublicApp {
    constructor() {
        this.registrationFormManager = null;
        this.init();
    }

    init() {
        try {
            this.initializeModules();
            this.bindHTMXEvents();
            // Ensure header/menu hx-trigger="load" elements are processed when
            // this module runs after DOMContentLoaded (type="module" is deferred).
            if (window.htmx && typeof window.htmx.process === 'function') {
                window.htmx.process(document.body);
            }
            // Set active menu links on initial load
            setActiveMenuLinks();
        } catch (error) {
            console.error('PublicApp: Initialization failed:', error);
        }
    }

    initializeModules() {
        // Initialize registration form manager
        this.registrationFormManager = new RegistrationFormManager();
        
        // Initialize modal loader (conditionally loads modal.js if needed)
        initModalLoader();
    }

    bindHTMXEvents() {
        // Listen for HTMX content swaps to re-initialize modules
        document.addEventListener('htmx:afterSwap', (event) => {
            this.handleContentSwap(event.target);
            // Re-set active menu links after HTMX swaps (for render_menu)
            setActiveMenuLinks(event.target);
        });
        
        // Also listen for navigation events
        document.addEventListener('htmx:afterRequest', () => {
            setActiveMenuLinks();
        });
    }

    handleContentSwap(target) {
        // Re-initialize registration form if registration form is loaded
        if (this.isRegistrationForm(target)) {
            if (this.registrationFormManager) {
                this.registrationFormManager.init();
            }
        }
    }

    isRegistrationForm(target) {
        return target.id === 'registration-form-container' ||
               target.querySelector('#registration-form-container') ||
               target.querySelector('#registration-type-select');
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        window.publicApp = new PublicApp();
    });
} else {
    // DOM already loaded
    console.log('PublicApp: DOM already loaded, starting initialization...');
    window.publicApp = new PublicApp();
}

